import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import {
  FaRegCheckCircle,
  FaRegQuestionCircle,
  FaThumbsUp,
  FaBan,
  FaCheckCircle,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Chart } from 'react-google-charts';

import { Container, Button, Row, Col, Table } from 'react-bootstrap';
import Input from '../../components/Input';
import Loading from '../../components/Loading';
import { useToast } from '../../hooks/toast';

import { CONFIRMACAO_AGENDA, STATUS_AGENDA } from '../../utils/funcoes';

import api from '../../services/api';

import { Title, CardIconContainer, CardHeader, SpaceBetween } from './styles';
import getResponseErrors from '../../utils/getResponseErrors';
import { useAuth } from '../../hooks/auth';

interface ITotalPorStatus {
  Total: number;
  AgeStatus: number;
}

interface IReturnAgendasDoDiaDTO {
  ConCodigo: number;
  ConNome: string;
  HorHoraIni: string;
  HorHoraFim: string;
  Agendamentos: number;
  Atendidos: number;
  Disponivel: boolean;
}

export interface IAgendamentosPorDia {
  dia: string;
  qtd: number;
}

interface IRetornoAgendamentosDashboardDTO {
  totais: ITotalPorStatus[];
  agendas: IReturnAgendasDoDiaDTO[];
  agendamentosSemana: IAgendamentosPorDia[];
}

interface ITotais {
  confirmados: number;
  aguardando: number;
  atendidos: number;
  cancelados: number;
}

interface IGenericType {
  [key: string]: any;
}

const Dashboard: React.FC = () => {
  const { addToast } = useToast();
  const { clinica } = useAuth();
  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dataFormatada, setDataFormatada] = useState(
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [dataExibeFormatada, setDataExibeFormatada] = useState(
    format(new Date(), 'dd/MM/yyyy'),
  );
  const [totais, setTotais] = useState<ITotais | undefined>(undefined);
  const [agendas, setAgendas] = useState<IReturnAgendasDoDiaDTO[]>([]);
  const [agendamentosSemana, setAgendamentosSemana] = useState<IGenericType[]>(
    [],
  );

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IRetornoAgendamentosDashboardDTO>(
          `agendamentos/dashboard?data=${dataFormatada}`,
        );
        const confirmados = response.data.totais.find(
          tot => tot.AgeStatus === CONFIRMACAO_AGENDA.CONFIRMADO + 1, // AgeConfirmado>0 === 2
        )?.Total;
        const aguardando = response.data.totais.find(
          tot =>
            tot.AgeStatus === CONFIRMACAO_AGENDA.AGUARDANDO_CONFIRMACAO + 1, // AgeConfirmado=0 === 1
        )?.Total;
        const atendidos = response.data.totais.find(
          tot => tot.AgeStatus === STATUS_AGENDA.ATENDIDO,
        )?.Total;
        const cancelados = response.data.totais.find(
          tot => tot.AgeStatus === STATUS_AGENDA.CANCELADO,
        )?.Total;
        setTotais({
          confirmados: confirmados || 0,
          aguardando: aguardando || 0,
          atendidos: atendidos || 0,
          cancelados: cancelados || 0,
        });
        setAgendas(response.data.agendas);
        const dadosGrafico: IGenericType[] = response.data.agendamentosSemana.map(
          age => {
            return [age.dia, age.qtd];
          },
        );
        const graficoLegenda: IGenericType[] = [['Dia', 'Qtd']];

        setAgendamentosSemana(graficoLegenda.concat(dadosGrafico));
      } catch (err) {
        const mensagemErro = getResponseErrors(err);
        addToast({
          title: 'Erro ao buscar dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    loadDados();
  }, [dataFormatada, addToast]);

  const handleSubmit = useCallback((dados: { data: Date }) => {
    const dataParsed = new Date(
      `${dados.data.toString().replace(/-/g, '/')} 03:00:00`,
    );
    setDataFormatada(format(dataParsed, 'yyyy-MM-dd'));
    setDataExibeFormatada(format(dataParsed, 'dd/MM/yyyy'));
  }, []);

  return (
    <Container>
      {loading && <Loading backgroundBlack={false} />}
      <SpaceBetween>
        <Title>Dashboard</Title>
        <Form
          ref={formRef}
          initialData={{ data: dataFormatada }}
          onSubmit={handleSubmit}
        >
          <div className="d-flex align-items-center">
            Data
            <div className="px-2">
              <Input type="date" name="data" required placeholder="Data" />
            </div>
            <Button type="submit" size="sm" variant="outline-primary">
              <FaCheckCircle />
            </Button>
          </div>
        </Form>
      </SpaceBetween>
      <br />

      <div className="card-deck">
        <div className="card shadow">
          <CardHeader className="card-header">
            <CardIconContainer className="bg-primary">
              <FaRegCheckCircle size={24} />
            </CardIconContainer>
            <p>Confirmados</p>
            <h3 className="card-title">
              <Link to={`agendabusca?status=2&data=${dataFormatada}`}>
                {totais?.confirmados}
              </Link>
            </h3>
          </CardHeader>
        </div>
        <div className="card shadow">
          <CardHeader className="card-header">
            <CardIconContainer className="bg-warning">
              <FaRegQuestionCircle size={24} />
            </CardIconContainer>
            <p>Aguardando Confirmação</p>
            <h3 className="card-title">
              <Link to={`agendabusca?status=1&data=${dataFormatada}`}>
                {totais?.aguardando}
              </Link>
            </h3>
          </CardHeader>
        </div>
        <div className="card shadow">
          <CardHeader className="card-header">
            <CardIconContainer className="bg-success">
              <FaThumbsUp size={24} />
            </CardIconContainer>
            <p>Atendidos</p>
            <h3 className="card-title">
              <Link to={`agendabusca?status=5&data=${dataFormatada}`}>
                {totais?.atendidos}
              </Link>
            </h3>
          </CardHeader>
        </div>
        <div className="card shadow">
          <CardHeader className="card-header">
            <CardIconContainer className="bg-danger">
              <FaBan size={24} />
            </CardIconContainer>
            <p>Cancelados</p>
            <h3 className="card-title">
              <Link to={`agendabusca?status=0&data=${dataFormatada}`}>
                {totais?.cancelados}
              </Link>
            </h3>
          </CardHeader>
        </div>
      </div>
      <div className="bg-white border rounded shadow p-2 my-3">
        <Row>
          <Col xs={12} md={5}>
            {agendamentosSemana && agendamentosSemana.length > 1 ? (
              <Chart
                chartType="ColumnChart"
                data={agendamentosSemana}
                options={{
                  title: 'Agendamentos da semana',
                  legend: 'none',
                }}
                width="100%"
                height="300px"
                legendToggle
              />
            ) : (
              <h6>Sem agendamentos na semana</h6>
            )}
          </Col>
          <Col xs={12} md={7}>
            <h6>
              {`Agendamentos de ${dataExibeFormatada}`}{' '}
              <Link
                to="agendaenvioconfirmacao"
                className="float-right btn btn-sm btn-primary mb-1"
              >
                Enviar confirmação
              </Link>
              {clinica.CliUsaPainel && (
                <Link
                  to="/controlepainel"
                  className="float-right btn btn-sm btn-primary mb-1 mr-2"
                >
                  Controles Painel
                </Link>
              )}
            </h6>
            {agendas && (
              <div
                className="table-responsive"
                style={{ maxHeight: '400px', overflow: 'auto' }}
              >
                <Table className="text-secondary" hover size="sm">
                  <thead>
                    <tr className="bg-light text-dark">
                      <th>Agenda</th>
                      <th>Agendamentos</th>
                      <th>Atendidos</th>
                      <th style={{ minWidth: '100px' }}>Horário</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agendas.map(age => (
                      <tr key={`con-${age.ConCodigo.toString()}`}>
                        <td>
                          <Link
                            to={`agenda/${age.ConCodigo}?data=${dataFormatada}`}
                            className="text-reset"
                          >
                            {age.ConNome}
                          </Link>
                        </td>
                        <td>{age.Agendamentos}</td>
                        <td>{age.Atendidos}</td>
                        <td> {`${age.HorHoraIni} as ${age.HorHoraFim}`}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default Dashboard;
